import { act } from "../jsx-act";
import { renderTaggedString, TagComponents } from "../jsx-act/tagged-string";
import UXDefinitions from "../definitions/ux-definitions";
import OpenIcon from "../ux-components/utilities/icon-open";
import Link from "../ux-components/utilities/link";
import * as ar from "../content-reef/ar.json";
import * as de from "../content-reef/de.json";
import * as en from "../content-reef/en.json";
import * as es from "../content-reef/es.json";
import * as fr from "../content-reef/fr.json";
import * as he from "../content-reef/he.json";
import * as id from "../content-reef/id.json";
import * as it from "../content-reef/it.json";
import * as ja from "../content-reef/ja.json";
import * as ko from "../content-reef/ko.json";
import * as ptBR from "../content-reef/pt-BR.json";
import * as ru from "../content-reef/ru.json";
import * as th from "../content-reef/th.json";
import * as trTR from "../content-reef/tr-TR.json";
import * as vi from "../content-reef/vi.json";
import * as zhCN from "../content-reef/zh-CN.json";
import * as zhTW from "../content-reef/zh-TW.json";

const COOKIE_LINK = "https://aws.amazon.com/legal/cookies/"
const EMAIL_FORM = "https://pulse.aws/application/ZRPLWLL6?p=0";
const COOKIE_NOTICE = "https://aws.amazon.com/legal/cookies/";
const PRIVACY_NOTICE = "https://aws.amazon.com/privacy/";

interface Entry { text: string }
interface Catalog { [key: string]: Entry | Catalog }

// Consumers address locales by these normalized codes (public API), ContentReef by BCP47 file name.
const catalogs: { [code in UXDefinitions.LanguageCodesNormalized]: Catalog } = {
  "ar-sa": ar,
  "en-us": en,
  "de-de": de,
  "es-es": es,
  "fr-fr": fr,
  "he-il": he,
  "id-id": id,
  "it-it": it,
  "ja-jp": ja,
  "ko-kr": ko,
  "pt-br": ptBR,
  "ru-ru": ru,
  "th-th": th,
  "tr-tr": trTR,
  "vi-vn": vi,
  "zh-cn": zhCN,
  "zh-tw": zhTW,
};

const RTL_LANGUAGES = ["ar", "fa", "he", "ur"];

// Values typed string|JSX.Element in UXDefinitions; they render inside a <span>.
const MARKUP_KEYS: { [group in keyof UXDefinitions.LocalizationSet]: string[] } = {
  consentBanner: ["paragraph", "paragraph-mobile"],
  consentSelector: ["footer", "footer-mobile"],
  ccbaSelector: ["optout-intro", "extra-info-p1", "extra-info-p2"],
  errorMessage: ["paragraph"],
};

const textOf = (value: Entry | Catalog | undefined) => value && (value as Entry).text;

// Kept out of ContentReef so it is never translated: only English shows the CCBA GPC warning.
const ENGLISH_CCBA_GPC_WARNING = {
  "gpc-warning-title": "Global Privacy Control Active",
  "gpc-warning-message": "Your browser's Global Privacy Control is enabled. Cross-context behavioral advertising is disabled to honor your privacy preference.",
};

// Pre-ContentReef Spanish-only title with no English source, so ContentReef can neither translate nor drop it.
const FOOTER_COOKIE_NOTICE_LINK_TITLES: { [code in UXDefinitions.LanguageCodesNormalized]?: string } = {
  "es-es": "Se abre en una ventana nueva",
};

// Translations arrive after English changes merge, so a key a locale lacks, or has in a different shape, falls back to English.
export function withFallback(catalog: Catalog, fallback: Catalog): Catalog {
  const merged: Catalog = { ...catalog };
  Object.keys(fallback).forEach(key => {
    const value = catalog[key];
    const valueIsGroup = value !== undefined && textOf(value) === undefined;
    if (textOf(fallback[key]) === undefined) {
      merged[key] = withFallback((valueIsGroup ? value : {}) as Catalog, fallback[key] as Catalog);
    } else if (value === undefined || valueIsGroup) {
      merged[key] = fallback[key];
    }
  });
  return merged;
}

function tagComponents(attributes: Catalog, cookieNoticeLinkTitle?: string): TagComponents {
  const externalLink = (dataId: string, href: string) => children =>
    act(Link, { "data-id": dataId, href, target: "_blank", rel: "noopener noreferrer", external: true }, ...children);
  return {
    br: () => act("br", null),
    openIcon: () => act(OpenIcon, { ariaLabel: textOf(attributes["open-in-new-tab"]), size: "10px" }),
    cookieNoticeLink: children => act("a", {
      "data-id": "awsccc-cs-f-notice",
      href: COOKIE_LINK,
      target: "_blank",
      rel: "noopener noreferrer",
      ...(cookieNoticeLinkTitle ? { title: cookieNoticeLinkTitle } : {}),
    }, ...children),
    cookieNoticeText: children => act("span", null, ...children),
    ccbaCookieNoticeLink: externalLink("awsccc-ccba-c-notice", COOKIE_NOTICE),
    emailFormLink: externalLink("awsccc-ccba-e-form", EMAIL_FORM),
    privacyNoticeLink: externalLink("awsccc-ccba-p-notice", PRIVACY_NOTICE),
  };
}

function toStrings(catalog: Catalog, markupKeys: string[], componentsFor: (key: string) => TagComponents) {
  const strings = {};
  Object.keys(catalog).forEach(key => {
    const value = catalog[key];
    const text = textOf(value);
    if (text === undefined) {
      strings[key] = toStrings(value as Catalog, [], componentsFor);
    } else {
      strings[key] = markupKeys.indexOf(key) > -1 ? act("span", null, ...renderTaggedString(text, componentsFor(key))) : text;
    }
  });
  return strings;
}

function toLocalizationSet(code: UXDefinitions.LanguageCodesNormalized, catalog: Catalog): UXDefinitions.LocalizationSet {
  const attributes = (catalog.attributes || {}) as Catalog;
  const components = tagComponents(attributes);
  const footerComponents = tagComponents(attributes, FOOTER_COOKIE_NOTICE_LINK_TITLES[code]);
  const set = {} as UXDefinitions.LocalizationSet;
  Object.keys(MARKUP_KEYS).forEach(group => {
    set[group] = toStrings(catalog[group] as Catalog, MARKUP_KEYS[group],
      key => group === "consentSelector" && key === "footer" ? footerComponents : components);
  });
  const { title, ...ccbaSelector } = set.ccbaSelector as any;
  set.ccbaSelector = { tittle: title, ...ccbaSelector, ...(code === "en-us" ? ENGLISH_CCBA_GPC_WARNING : {}) };
  return set;
}

export const CONTENT_REEF_TAGS = Object.keys(tagComponents({}));

export const localizationDictionary: UXDefinitions.LocalizationDictionary = {};
export const localizationRtl: UXDefinitions.LanguageCodesNormalized[] = [];
(Object.keys(catalogs) as UXDefinitions.LanguageCodesNormalized[]).forEach(code => {
  localizationDictionary[code] = toLocalizationSet(code, withFallback(catalogs[code], en));
  if (RTL_LANGUAGES.indexOf(code.split("-")[0]) > -1) {
    localizationRtl.push(code);
  }
});
